// Rendering a commission flag — the pure parts.
//
// ── Why this exists ───────────────────────────────────────────────────────
// comm_flags is gaining a second kind of row. An 'entry' flag disputes a
// specific entry on the rep's own tracker. A 'missing_invoice' flag raises an
// invoice that is NOT on their tracker — missed, or commissioned to the wrong
// rep — and by design it carries NO entry_id, because the whole point is that
// the rep cannot see a row to attach to.
//
// The consequence for this view: a missing_invoice flag has no customer and
// often no amount, because the rep is telling us what they believe rather than
// reading it off a record. Comm Review renders every field straight off the
// flag row and never joins to entries, so those nulls arrive here untouched.
//
// ── The specific bug this prevents ────────────────────────────────────────
// fmt(null) is "$0.00". A missing_invoice flag with no amount would have
// rendered as a confident zero — a number the admin has no reason to doubt and
// which is not true. The toolbox already names this exact hazard on the rep's
// side of the same feature (MyCommissions.jsx, the missing-rate panel):
//
//   "the number that must never appear is a confident $0.00. A rep who sees
//    $0.00 believes it; a rep who sees an em dash and this notice asks about
//    it."
//
// Same trap, admin side. An absent amount must render as an em dash and a
// present zero must still render as $0.00 — which is why this is a predicate
// on presence rather than a truthiness check.
//
// ── Forward compatibility is deliberate ───────────────────────────────────
// flagTypeOf defaults to 'entry' when flag_type is absent. That is what lets
// this view deploy BEFORE migration 074 adds the column: every existing row is
// an entry flag, so the default is not a fallback, it is the correct answer for
// every row that can exist pre-074. Ship this first, and the first
// missing_invoice flag ever filed lands in a view that can already read it.
//
// These live outside App.js so the rules can be tested without rendering the
// app; nothing here touches Supabase.

export const FLAG_TYPE_ENTRY = 'entry'
export const FLAG_TYPE_MISSING_INVOICE = 'missing_invoice'

/** What the admin needs to know before deciding what to do about a flag. */
export const FLAG_TYPE_META = {
  [FLAG_TYPE_ENTRY]: {
    label: 'Entry',
    bg: '#f5f5f3', color: '#666', border: '#ddd',
  },
  [FLAG_TYPE_MISSING_INVOICE]: {
    label: 'Missing invoice',
    bg: '#F5ECFA', color: '#6B21A8', border: '#D8B4FE',
  },
}

/**
 * The flag's type, always one of the two known values.
 *
 * Defaults to 'entry' for a row with no flag_type — which is every row that
 * exists before migration 074, and every row a pre-074 client writes. An
 * unrecognised value also lands on 'entry' rather than rendering a blank badge:
 * the database's comm_flags_shape constraint makes a third value impossible, so
 * seeing one here means something is already wrong, and the least-alarming
 * honest label beats an empty cell.
 */
export function flagTypeOf(flag) {
  return flag && flag.flag_type === FLAG_TYPE_MISSING_INVOICE
    ? FLAG_TYPE_MISSING_INVOICE
    : FLAG_TYPE_ENTRY
}

/** Is this a flag about an invoice the rep cannot see on their tracker? */
export function isMissingInvoiceFlag(flag) {
  return flagTypeOf(flag) === FLAG_TYPE_MISSING_INVOICE
}

/**
 * Does this flag carry a real amount?
 *
 * A presence check, NOT truthiness. A genuine $0.00 — a rep disputing a zeroed
 * commission is a real case — must still render as $0.00. Only null, undefined,
 * empty string and unparseable values are absent.
 *
 * Numeric columns arrive from PostgREST as strings on some paths and numbers on
 * others, so this accepts both rather than depending on which.
 */
export function hasAmount(amount) {
  if (amount === null || amount === undefined || amount === '') return false
  return Number.isFinite(Number(amount))
}

/** Text for a field the rep may simply not have known. Never a blank cell. */
export function displayText(value) {
  if (value === null || value === undefined) return '—'
  const s = String(value).trim()
  return s === '' ? '—' : s
}

/**
 * The invoice number as the admin should read it.
 *
 * On a missing_invoice flag this is the rep's claim typed by hand, so it may be
 * anything — including blank, if a client ever writes one past the constraint.
 * The '#' prefix is added by the caller only when there is something to prefix.
 */
export function displayInvoice(flag) {
  return displayText(flag && flag.invoice)
}
